import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@/lib/query";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { ReviewStep } from "@/components/doctor/onboarding/review-step";
import {
  BasicsStep,
  ConsultationStep,
  EducationStep,
  EstablishmentProofStep,
  EstablishmentStep,
  IdentityProofStep,
  PracticeStep,
  RegistrationProofStep,
  RegistrationStep,
  SpecializationStep,
} from "@/components/doctor/onboarding/steps-profile";
import {
  ConsultantStep,
  LocationStep,
  TimingsStep,
} from "@/components/doctor/onboarding/steps-practice";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ApiError } from "@/lib/api/client";
import { doctorApi, doctorKeys } from "@/lib/api/doctorApi";
import { cn } from "@/lib/utils";
import { OnboardingProvider, useOnboarding } from "@/hooks/doctor/use-onboarding";
import { validateStep, type StepId } from "@/services/onboarding/validation";
import type { RegisterDoctorInput, ScheduleSlot } from "@/types/doctor";
import type { OnboardingDraft } from "@/types/onboarding";

const STEP_CONTENT: Record<StepId, () => React.JSX.Element> = {
  basics: BasicsStep,
  specialization: SpecializationStep,
  education: EducationStep,
  practice: PracticeStep,
  establishment: EstablishmentStep,
  registration: RegistrationStep,
  consultation: ConsultationStep,
  "identity-proof": IdentityProofStep,
  "registration-proof": RegistrationProofStep,
  "establishment-proof": EstablishmentProofStep,
  location: LocationStep,
  timings: TimingsStep,
  consultant: ConsultantStep,
  review: ReviewStep,
};

const STEP_DESCRIPTION: Record<StepId, string> = {
  basics: "Tell us who you are and where you practise.",
  specialization: "What patients should find you for.",
  education: "Your qualification and how long you've practised.",
  practice: "Own establishment or visiting consultant.",
  establishment: "Where your practice is based.",
  registration: "Your council registration details.",
  consultation: "Fees and how patients can consult you.",
  "identity-proof": "A government photo ID confirms who you are.",
  "registration-proof": "Proof of your medical registration.",
  "establishment-proof": "Proof of your relationship with the establishment.",
  location: "Help patients find you on the map.",
  timings: "When you see patients each week.",
  consultant: "Optional second practice with its own fees.",
  review: "One last look before you submit.",
};

/** Maps the draft onto the documented POST /api/doctors/register payload. */
export function draftToRegisterInput(draft: OnboardingDraft): RegisterDoctorInput {
  const schedule: ScheduleSlot[] = draft.timings
    .filter((day) => day.enabled && day.sessions[0])
    .map((day) => ({
      day: day.day,
      startTime: day.sessions[0]!.from,
      endTime: day.sessions[0]!.to,
      isAvailable: true,
    }));

  const qualification = [draft.degree, draft.institute].filter(Boolean).join(" — ");

  return {
    name: draft.fullName.trim(),
    specialization: draft.specialization.trim(),
    experience: Number(draft.experience) || 0,
    fees: Number(draft.fees) || 0,
    location: [draft.locality, draft.establishmentCity || draft.city, draft.region]
      .filter(Boolean)
      .join(", "),
    hospitalName: draft.establishmentName.trim() || null,
    phone: draft.phone.trim() || null,
    bio: draft.bio.trim() || null,
    profilePicture: null,
    videoConsultationAvailable: draft.videoConsultationAvailable,
    qualifications: qualification ? [qualification] : [],
    languages: draft.languages,
    schedule,
  };
}

function StepRail() {
  const { steps, stepIndex, goTo, draft } = useOnboarding();
  const sections = useMemo(() => {
    const grouped = new Map<string, { index: number; title: string; id: StepId }[]>();
    steps.forEach((step, index) => {
      const list = grouped.get(step.section) ?? [];
      list.push({ index, title: step.title, id: step.id });
      grouped.set(step.section, list);
    });
    return [...grouped.entries()];
  }, [steps]);

  return (
    <nav aria-label="Onboarding steps" className="space-y-6">
      {sections.map(([section, entries]) => (
        <div key={section}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {section}
          </p>
          <ol className="space-y-1">
            {entries.map((entry) => {
              const done =
                entry.index < stepIndex && Object.keys(validateStep(entry.id, draft)).length === 0;
              const active = entry.index === stepIndex;
              return (
                <li key={entry.id}>
                  <button
                    type="button"
                    onClick={() => goTo(entry.index)}
                    aria-current={active ? "step" : undefined}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                      active
                        ? "bg-primary/10 font-medium text-primary"
                        : "text-muted-foreground hover:bg-muted",
                    )}
                  >
                    <span
                      className={cn(
                        "grid size-5 shrink-0 place-items-center rounded-full border text-[0.625rem]",
                        done
                          ? "border-primary bg-primary text-primary-foreground"
                          : active
                            ? "border-primary text-primary"
                            : "border-border",
                      )}
                    >
                      {done ? <Check className="size-3" /> : entry.index + 1}
                    </span>
                    <span className="truncate">{entry.title}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </nav>
  );
}

function WizardBody({ onSuccess }: { onSuccess?: (() => void) | undefined }) {
  const { draft, step, stepIndex, steps, next, back, isFirst, isLast, progress, goTo, hydrated } =
    useOnboarding();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => doctorApi.register(draftToRegisterInput(draft)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: doctorKeys.me });
      toast.success("Registration submitted for verification.");
      onSuccess?.();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "We couldn't submit your registration.");
    },
  });

  const submit = () => {
    setError(null);
    const blocking = steps
      .map((entry) => ({ id: entry.id, errors: validateStep(entry.id, draft) }))
      .find((entry) => Object.keys(entry.errors).length > 0);
    if (blocking) {
      const index = steps.findIndex((entry) => entry.id === blocking.id);
      goTo(index);
      setError(
        "Some details are still missing. We've taken you to the section that needs attention.",
      );
      return;
    }
    mutation.mutate();
  };

  const StepComponent = STEP_CONTENT[step.id];

  if (!hydrated) {
    return (
      <div className="grid min-h-[24rem] place-items-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[16rem_1fr]">
      <aside className="hidden lg:block">
        <div className="sticky top-24 card-surface p-5">
          <StepRail />
        </div>
      </aside>

      <div className="space-y-5">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Step {stepIndex + 1} of {steps.length} · {step.section}
            </span>
            <span>{progress}% complete</span>
          </div>
          <Progress value={progress} className="h-1.5" />
        </div>

        <section className="card-surface p-6 sm:p-7">
          <header className="mb-6">
            <h2 className="font-display text-xl font-semibold">{step.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{STEP_DESCRIPTION[step.id]}</p>
          </header>

          <StepComponent />

          {error && (
            <p
              role="alert"
              className="mt-5 rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              {error}
            </p>
          )}

          <footer className="mt-7 flex items-center gap-3 border-t border-border pt-5">
            <Button type="button" variant="ghost" onClick={back} disabled={isFirst}>
              <ArrowLeft className="size-4" /> Back
            </Button>
            <div className="ml-auto">
              {isLast ? (
                <Button type="button" onClick={submit} disabled={mutation.isPending}>
                  {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
                  Submit for verification
                </Button>
              ) : (
                <Button type="button" onClick={() => next()}>
                  Continue <ArrowRight className="size-4" />
                </Button>
              )}
            </div>
          </footer>
        </section>

        <p className="text-center text-xs text-muted-foreground">
          Your progress is saved on this device as you go.
        </p>
      </div>
    </div>
  );
}

export function OnboardingWizard({
  onSuccess,
  initial,
}: {
  onSuccess?: (() => void) | undefined;
  initial?: Partial<OnboardingDraft> | undefined;
}) {
  return (
    <OnboardingProvider initial={initial}>
      <WizardBody onSuccess={onSuccess} />
    </OnboardingProvider>
  );
}
